/**
 * Wealthica2 multi-institution account fetch actions
 *
 * Responsibilities:
 * - Cache read-through on navigation for fast UI hydration
 * - TTL-aware gating and deduplication of concurrent fetches
 * - Error propagation to store; conditional persistence handoff
 * - Optional awaited persistence sequencing (accounts → positions)
 *
 * Notes:
 * - Mutates the multi-store via injected set/get.
 * - Does not implement persistence itself; delegates via store actions.
 */
import { toast } from 'sonner';
import { ACCOUNTS_TTL_MS, POSITIONS_TTL_MS } from '../../utils/query-config';
import { shouldFetchAccounts, shouldSkipPositions } from '../utils/account-decisions';
import { isConnectionBusy } from '../utils/connection-flags';

export const createAccountFetchActions = (set: any, get: any) => ({
  // === MULTI-INSTITUTION ACCOUNT FETCHING ===
  fetchAccountsForInstitution: async (
    institutionId: string,
    isNavigation = false,
    forceRefresh = false,
    persistOptions?: { toastId?: string | number; suppressSuccessToast?: boolean; awaitPersistence?: boolean }
  ) => {
    const state = get();
    const connection = state.connections.get(institutionId);
    
    if (!connection) {
      console.warn('⚠️ MULTI-ACCOUNTS: No connection found for institution', { institutionId });
      return;
    }

    // Instant cache read-through on navigation: populate accounts from cache for fast UI
    try {
      if (isNavigation && (!connection.accounts || connection.accounts.length === 0)) {
        const { getCachedAccountsForInstitution } = await import('../../utils/cache-management');
        const cached = getCachedAccountsForInstitution<any>(institutionId, true);
        if (cached && Array.isArray(cached) && cached.length > 0) {
          // Populate store with cached accounts to render immediately; network fetch will reconcile
          get().setAccountsForInstitution(institutionId, cached as any[]);
        }
      }
    } catch { /* noop */ }

    // DEDUPLICATION: If an accounts fetch or persistence is already in progress, skip
    try {
      if (isConnectionBusy(connection)) {
        return;
      }
    } catch { /* noop */ }

    // Check if we need to fetch accounts (cache/ttl aware)
    if (!shouldFetchAccounts(connection, forceRefresh)) {
      return;
    }
    
    get().setAccountsLoadingForInstitution(institutionId, true);
    get().setAccountsErrorForInstitution(institutionId, null);

    try {
      // Use WealthicaDataService instead of calling edge function directly
      const { WealthicaDataService } = await import('../../services/WealthicaDataService');
      
      const result = await WealthicaDataService.getAccounts({
        institutionId,
        providerName: connection.providerName,
        bearerToken: connection.bearerToken
      });

      if (!result.success || !result.data) {
        const errMsg = String(result.error || '').toLowerCase();
        const tokenMissing = errMsg.includes('no bearer token available');
        if (tokenMissing && isNavigation) {
          console.warn('⏭️ MULTI-CONN: Token unavailable during background fetch', { institutionId });
        } else {
          console.error('❌ MULTI-CONN: Service failed', { institutionId, error: result.error });
        }
        throw new Error(result.error || 'Failed to fetch accounts from service');
      }

      const accounts = result.data;
      
      // Update accounts for this specific institution
      get().setAccountsForInstitution(institutionId, accounts);

      // Trigger positions loading (optionally await persistence if requested)
      if (accounts.length > 0) {
        // Optimization: skip positions for cash-only institutions with no hints when previously fetched
        if (shouldSkipPositions(accounts, connection)) {
          console.debug('⏩ MULTI-CONN: Skipping positions fetch (cash-only institution, no holdings detected)', {
            institutionId,
            accounts: accounts.length
          });

          // Persist accounts even when positions are intentionally skipped
          try {
            const persistDataPreference = get().persistDataPreference;
            if (persistDataPreference) {
              const { useAuthStore } = await import('@/stores/authStore');
              const user = useAuthStore.getState().user;
              if (user?.id && connection?.connectionData && !connection.isPersisting) {
                const shouldAwait = !!persistOptions?.awaitPersistence;
                const toastId = persistOptions?.toastId;
                const suppressSuccess =
                  persistOptions?.suppressSuccessToast ?? (!toastId); // default: suppress unless a toastId is provided

                const persistPromise = get().persistAccountsAndPositions(
                  user.id,
                  connection.connectionData,
                  accounts,
                  { isRefresh: !!forceRefresh, toastId, suppressSuccessToast: suppressSuccess }
                );

                if (shouldAwait) {
                  await persistPromise;
                  // No positions to follow; if we were using a spinner toast, finalize it to success
                  if (toastId) {
                    try {
                      toast.success('Successfully saved accounts', { id: toastId, description: `Accounts: ${accounts.length}`, duration: 3000 });
                    } catch { /* noop */ }
                  }
                } else {
                  persistPromise.catch((persistError: unknown) => {
                    console.error('⚠️ MULTI-CONN: Accounts-only persistence failed (cash-only skip)', { institutionId, persistError });
                  });
                }
              }
            }
          } catch (persistErr) {
            console.warn('⚠️ MULTI-CONN: Failed to start accounts-only persistence on skip', { institutionId, persistErr });
          }
        } else {
          try {
            const shouldAwait = !!persistOptions?.awaitPersistence;

            if (shouldAwait) {
              // Persist ACCOUNTS immediately (do not block on positions)
              try {
                const persistDataPreference = get().persistDataPreference;
                if (persistDataPreference) {
                  const { useAuthStore } = await import('@/stores/authStore');
                  const user = useAuthStore.getState().user;
                  if (user?.id && connection?.connectionData && !connection.isPersisting) {
                    await get().persistAccountsAndPositions(
                      user.id,
                      connection.connectionData,
                      accounts,
                      { isRefresh: !!forceRefresh, toastId: persistOptions?.toastId, suppressSuccessToast: true, mode: 'accounts_only' }
                    );
                  }
                }
              } catch (persistStartErr) {
                console.warn('⚠️ MULTI-CONN: Failed to run awaited accounts persistence', { institutionId, persistStartErr });
              }

              // Then load positions and persist them if present
              await get().loadPositionsForInstitution(institutionId);
              const connectionAfterPos = get().getConnection(institutionId);
              const positions = connectionAfterPos?.positions || [];
              console.debug('✅ MULTI-CONN: Positions loaded (awaited)', {
                institutionId,
                positionCount: positions.length
              });
              try {
                const persistDataPreference = get().persistDataPreference;
                if (persistDataPreference && positions.length > 0 && connectionAfterPos?.connectionData && !connectionAfterPos.isPersisting) {
                  const { useAuthStore } = await import('@/stores/authStore');
                  const user = useAuthStore.getState().user;
                  if (user?.id) {
                    await get().persistAccountsAndPositions(
                      user.id,
                      connectionAfterPos.connectionData,
                      accounts,
                      { isRefresh: !!forceRefresh, toastId: persistOptions?.toastId, suppressSuccessToast: !persistOptions?.toastId, mode: 'positions_only' }
                    );
                  }
                }
              } catch (persistStartErr) {
                console.warn('⚠️ MULTI-CONN: Failed to run awaited positions persistence', { institutionId, persistStartErr });
              }
            } else {
              // Fire-and-forget: persist accounts now, then positions when available.
              // IMPORTANT: keep a handle to the accounts-only persistence promise so we can
              // await it before attempting positions-only to avoid the isPersisting guard race.
              let accountsPersistPromise: Promise<void> | null = null;
              try {
                const persistDataPreference = get().persistDataPreference;
                if (persistDataPreference) {
                  const { useAuthStore } = await import('@/stores/authStore');
                  const user = useAuthStore.getState().user;
                  if (user?.id && connection?.connectionData && !connection.isPersisting) {
                    accountsPersistPromise = get().persistAccountsAndPositions(
                      user.id,
                      connection.connectionData,
                      accounts,
                      { isRefresh: !!forceRefresh, toastId: persistOptions?.toastId, suppressSuccessToast: persistOptions?.suppressSuccessToast, mode: 'accounts_only' }
                    );
                    // Do not await here; run in background but capture promise
                    accountsPersistPromise.catch((persistErr: unknown) => {
                      console.error('⚠️ MULTI-CONN: Accounts-only persistence failed (background)', { institutionId, persistErr });
                    });
                  }
                }
              } catch (persistErr) {
                console.error('⚠️ MULTI-CONN: Accounts-only persistence failed (background)', { institutionId, persistErr });
              }

              get().loadPositionsForInstitution(institutionId)
                .then(async () => {
                  const connectionAfterPos = get().getConnection(institutionId);
                  const positions = connectionAfterPos?.positions || [];
                  console.debug('✅ MULTI-CONN: Positions loaded (background)', {
                    institutionId,
                    positionCount: positions.length
                  });

                  try {
                    const persistDataPreference = get().persistDataPreference;
                    if (!persistDataPreference) return;

                    // Ensure any in-flight accounts-only persistence finishes first
                    if (accountsPersistPromise) {
                      try { await accountsPersistPromise; } catch { /* noop */ }
                    }

                    const { useAuthStore } = await import('@/stores/authStore');
                    const user = useAuthStore.getState().user;
                    if (!user?.id) return;
                    if (connectionAfterPos?.connectionData && !connectionAfterPos.isPersisting && positions.length > 0) {
                      get()
                        .persistAccountsAndPositions(
                          user.id,
                          connectionAfterPos.connectionData,
                          accounts,
                          { isRefresh: !!forceRefresh, toastId: persistOptions?.toastId, suppressSuccessToast: !persistOptions?.toastId, mode: 'positions_only' }
                        )
                        .catch((persistError: unknown) => {
                          console.error('⚠️ MULTI-CONN: Positions-only persistence failed (background)', { institutionId, persistError });
                        });
                    } else if (connectionAfterPos?.connectionData && connectionAfterPos.isPersisting && positions.length > 0) {
                      // Fallback: another accounts-only persistence is still running (e.g., started elsewhere).
                      // Wait for the completion event or a short timeout, then attempt positions-only persist once.
                      try {
                        console.debug('⏳ MULTI-CONN: Waiting for accounts persistence to finish before positions save', { institutionId });
                        const waitForPersistComplete = () => new Promise<void>((resolve) => {
                          const timeout = setTimeout(() => {
                            try { window.removeEventListener('wealthica:persist:completed', onComplete as any); } catch {}
                            resolve();
                          }, 15000); // 15s safety cap
                          const onComplete = (evt: Event) => {
                            const ce = evt as CustomEvent;
                            const match = (ce as any)?.detail?.institutionId === institutionId;
                            if (match) {
                              try { window.removeEventListener('wealthica:persist:completed', onComplete as any); } catch {}
                              clearTimeout(timeout);
                              resolve();
                            }
                          };
                          try { window.addEventListener('wealthica:persist:completed', onComplete as any, { once: true }); } catch { resolve(); }
                        });
                        await waitForPersistComplete();

                        const afterWait = get().getConnection(institutionId);
                        const posAfterWait = afterWait?.positions || [];
                        if (afterWait?.connectionData && !afterWait.isPersisting && posAfterWait.length > 0) {
                          get()
                            .persistAccountsAndPositions(
                              user.id,
                              afterWait.connectionData,
                              accounts,
                              { isRefresh: !!forceRefresh, toastId: persistOptions?.toastId, suppressSuccessToast: !persistOptions?.toastId, mode: 'positions_only' }
                            )
                            .catch((persistError: unknown) => {
                              console.error('⚠️ MULTI-CONN: Positions-only persistence failed (post-wait)', { institutionId, persistError });
                            });
                        }
                      } catch (waitErr) {
                        console.warn('⚠️ MULTI-CONN: Wait for accounts persistence finished with warning', { institutionId, waitErr });
                      }
                    }
                  } catch (persistStartErr) {
                    console.warn('⚠️ MULTI-CONN: Failed to start background positions persistence', { institutionId, persistStartErr });
                  }
                })
                .catch((posError: unknown) => {
                  console.warn('⚠️ MULTI-CONN: Position loading failed (background)', { institutionId, posError });
                });
            }
          } catch (posErr) {
            console.warn('⚠️ MULTI-CONN: Failed to start position load', { institutionId, posErr });
          }
        }
      }

      // Quieter UX: no success toast here; UI updates reflect the change

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const tokenMissing = String(message).toLowerCase().includes('no bearer token available');
      if (tokenMissing && isNavigation) {
        console.warn('⏭️ MULTI-CONN: Token unavailable during background fetch', { institutionId });
      } else {
        console.error('❌ MULTI-CONN: Fetch failed', { institutionId, error: message });
      }
      get().setAccountsErrorForInstitution(institutionId, message);
      
      if (!isNavigation) {
        toast.error(`❌ Failed to load accounts`);
      }
    } finally {
      get().setAccountsLoadingForInstitution(institutionId, false);
    }
  },

  // === REFRESH SPECIFIC INSTITUTION ===
  // Use isNavigation=true to suppress the intermediate "accounts loaded" toast.
  // The refresh UX will show a single final confirmation after persistence.
  refreshAccountsForInstitution: async (institutionId: string, options?: { toastId?: string | number }) => {
    const toastId = options?.toastId;
    // DEDUPLICATION: If a refresh/persist is already in progress for this institution, do not start another
    try {
      const current = get().getConnection?.(institutionId);
      const busy = !!current?.isLoadingAccounts || !!current?.isPersisting || current?.processingState === 'processing';
      if (busy) {
        return;
      }
    } catch { /* noop */ }
    try {
      // Suppress success toast from persistence and ensure loading toast is dismissed
      await get().fetchAccountsForInstitution(institutionId, true, true, { toastId, suppressSuccessToast: false, awaitPersistence: true });

      // If persistence is OFF, there will be no persistence upgrade/dismiss of the loading toast.
      // In that case, upgrade the loading toast to a success toast here to avoid hanging spinners.
      const state = get();
      const persistDataPreference: boolean = !!state.persistDataPreference;
      if (toastId && !persistDataPreference) {
        // Quieter UX: simply dismiss the loading toast without a success message
        toast.dismiss(toastId);
      }
    } catch (err) {
      // Ensure loading toast is upgraded to error to avoid hanging spinner on failure
      if (toastId) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Refresh failed', {
          id: toastId,
          description: message
        });
      }
      throw err;
    }
  },
});
